// 시안 v6 「챗봇 운영 설정」(pg-ops · renderOps · #opsSave). 계약 §12 — 대표 전용.
// 「설정 저장」 하나로 설정(PATCH /system/setting)과 카테고리 목록 전체(PUT /system/quick-categories)를 보낸다.
import { useEffect, useState } from "react";
import AdminGate from "../../v6common/AdminGate";
import { isOwner } from "../../v6common/auth";
import { errorText } from "../../inquiry/v6Api";
import { showToast } from "../../../utill/utill";
import { getSetting, saveSetting, getQuickCategories, putQuickCategories, getMeta } from "./api";
import { HOURS, SESSIONS, MAX_MESSAGES, settingForm, toRow, toPayload } from "./view";
import CategoryList from "./CategoryList";
import "../../../admin_styles/inquiry_renewal.css";
import "../../../admin_styles/admin_v6.css";
import "./ops.css";

function Field({ id, label, help, value, onChange, area }) {
    return (
        <div className="fld"><label htmlFor={id}>{label}</label>
            {area
                ? <textarea className="ta" id={id} style={{ minHeight: 70 }} value={value} onChange={(e) => onChange(e.target.value)} />
                : <input className="inp" id={id} style={{ width: "100%" }} value={value} onChange={(e) => onChange(e.target.value)} />}
            <div className="help">{help}</div></div>
    );
}

function Choice({ id, label, help, opts, value, onChange }) {
    return (
        <div className="fld"><label htmlFor={id}>{label}</label>
            <select className="sel" id={id} style={{ width: "100%" }} value={value} onChange={(e) => onChange(e.target.value)}>
                {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <div className="help">{help}</div></div>
    );
}

function Top({ children }) {
    return (
        <div className="top">
            <div><h1>챗봇 운영 설정</h1><p>가맹점주가 보는 첫 화면과 빠른 지원 카테고리, 연락처를 설정합니다</p></div>
            {children && <div className="top-act">{children}</div>}
        </div>
    );
}

export default function OpsPage({ go }) {
    // 통과하면 본문이 「설정 저장」이 달린 머리를 그린다. 막히면 머리만 두고 문 안내.
    return (
        <main className="inq-r">
            {!isOwner() && <Top />}
            <AdminGate ownerOnly>{() => <OpsBody />}</AdminGate>
        </main>
    );
}

function OpsBody() {
    const [loadErr, setLoadErr] = useState("");
    const [exists, setExists] = useState(true);
    const [form, setForm] = useState(null);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([getSetting(), getQuickCategories(), getMeta().catch(() => null)])
            .then(([s, cats, m]) => {
                if (!alive) return;
                setExists(!!s); setForm(settingForm(s)); setRows((cats || []).map(toRow)); setMeta(m);
            })
            .catch((e) => { if (alive) setLoadErr(errorText(e)); });
        return () => { alive = false; };
    }, []);

    const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
    const changeRows = (next) => { setRows(next); setDirty(true); };

    const save = async () => {
        const empty = rows.filter((r) => !r.name.trim()).length;
        if (empty) { showToast(`이름이 비어 있는 카테고리가 ${empty}개 있어요. 이름을 넣거나 삭제해 주세요.`, "warning"); return; }
        setSaving(true);
        try {
            const savedSetting = await saveSetting({ ...form }, exists);
            setExists(true);
            if (savedSetting) setForm(settingForm(savedSetting));
            const savedCats = await putQuickCategories(rows.map(toPayload));
            setRows((savedCats || []).map(toRow));
            setDirty(false);
            showToast("운영 설정을 저장했습니다. 가맹점주 앱에 바로 반영됩니다.", "success");
        } catch (e) {
            showToast(errorText(e), "error");
        } finally {
            setSaving(false);
        }
    };

    if (loadErr) return <><Top /><div className="actorbar" style={{ marginTop: 24 }}><i className="fa-solid fa-triangle-exclamation"></i>{loadErr}</div></>;
    if (!form) return <><Top /><div className="empty">불러오는 중…</div></>;

    return (
        <>
            <Top>
                {dirty && <span className="ops-dirty">저장하지 않은 변경이 있습니다</span>}
                <button type="button" className="btn btn-pri" id="opsSave" disabled={saving} onClick={save}>
                    <i className="fa-solid fa-floppy-disk"></i>설정 저장</button>
            </Top>
            <div className="cols2">
                <div className="fsec" style={{ margin: 0 }}><h3><i className="fa-solid fa-house"></i>환영 메시지</h3>
                    <Field id="opTitle" label="환영 제목" help="가맹점주가 챗봇을 열면 처음 보는 문장" value={form.welcome_title} onChange={set("welcome_title")} />
                    <Field id="opBody" label="환영 내용" help="환영 제목 아래 표시되는 설명" area value={form.welcome_message} onChange={set("welcome_message")} />
                    <Field id="opTitleZh" label="환영 제목 (中文)" help="중국어 화면에서 표시되는 제목 · 비우면 한국어 표시" value={form.welcome_title_zh} onChange={set("welcome_title_zh")} />
                    <Field id="opBodyZh" label="환영 내용 (中文)" help="중국어 화면의 설명" area value={form.welcome_message_zh} onChange={set("welcome_message_zh")} />
                </div>
                <div>
                    <div className="fsec"><h3><i className="fa-solid fa-phone"></i>긴급 연락처</h3>
                        <Field id="opTel" label="기술지원 전화번호" help="챗봇 화면의 [전화하기] 버튼에 연결되는 번호" value={form.emergency_phone} onChange={set("emergency_phone")} />
                        <Field id="opMail" label="문의 접수 이메일" help="매출 내역 등 자료 발송 요청의 발신 주소" value={form.emergency_email} onChange={set("emergency_email")} />
                        <Choice id="opHours" label="운영 시간" opts={HOURS} value={form.operating_hours} onChange={set("operating_hours")}
                            help={"운영 시간 밖에는 \"접수만 되고 다음 영업일에 처리\" 안내가 붙습니다"} />
                    </div>
                    <div className="fsec" style={{ margin: 0 }}><h3><i className="fa-solid fa-gear"></i>기본 동작 설정</h3>
                        <Choice id="opSession" label="세션 유지 시간" opts={SESSIONS} value={form.session_duration} onChange={set("session_duration")} help="이 시간 동안 말이 없으면 새 대화로 시작합니다" />
                        <Choice id="opMax" label="최대 메시지 수" opts={MAX_MESSAGES} value={form.max_messages} onChange={set("max_messages")} help="한 대화에서 주고받을 수 있는 최대 메시지 수" />
                    </div>
                </div>
            </div>
            <div className="fsec mt" style={{ marginBottom: 0 }}>
                <h3><i className="fa-solid fa-table-cells-large"></i>빠른 지원 카테고리 <span className="hint" style={{ fontWeight: 400, color: "var(--tx3)", fontSize: 12 }}>가맹점주가 버튼을 누르면 그 카테고리로 대화가 시작되고, 문의로 넘어갈 때 「접수 시 구분」이 자동으로 붙습니다</span></h3>
                <CategoryList rows={rows} meta={meta} onChange={changeRows} />
            </div>
        </>
    );
}
